import { styled } from '@nykaa/ui-components';
import { keyframes } from '@emotion/core';

const slideIn = keyframes`
  from {
    transform: translateX(-50%) translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateX(-50%) translateY(0);
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
    transform: translateY(20px);
  }
`;

interface IBannerContainerProps {
  isCollapsed?: boolean;
  isClosing?: boolean;
}

export const BannerContainer = styled.button<IBannerContainerProps>`
  position: fixed;
  bottom: calc(67px + env(safe-area-inset-bottom, 0px));
  z-index: calc(var(--overlay-z-index, 1000) + 1);

  ${({ isCollapsed, theme }) =>
    isCollapsed
      ? `
    left: auto;
    right: ${theme.spacing.spacing80};
    transform: translateX(0);
  `
      : `
    left: 50%;
    transform: translateX(-50%);
  `}

  height: 48px;
  padding: ${({ isCollapsed, theme }) =>
    isCollapsed 
      ? theme.spacing.spacing20
      : `0 ${theme.spacing.spacing60} 0 ${theme.spacing.spacing20}`};

  background-color: ${({ theme }) => theme.colors.textPrimary};
  border: 1.25px solid ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borders.radius50};
  cursor: pointer;

  display: flex;
  align-items: center;
  gap: ${({ isCollapsed, theme }) => (isCollapsed ? '0' : theme.spacing.spacing40)};

  animation: ${({ isCollapsed, isClosing }) => {
    if (isClosing) return `${fadeOut} 0.3s ease-out forwards`;
    if (isCollapsed) return 'none';
    return `${slideIn} 0.3s ease-out`;
  }};
  transition: ${({ isCollapsed }) =>
    isCollapsed
      ? 'left 0.4s ease-in-out, right 0.4s ease-in-out, transform 0.4s ease-in-out, padding 0.3s ease-in-out, width 0.3s ease-in-out, gap 0.3s ease-in-out'
      : 'none'};

  outline: none;

  &:active {
    transform: ${({ isCollapsed }) =>
      isCollapsed ? 'scale(0.98)' : 'translateX(-50%) scale(0.98)'};
  }
`;

/**
 * Logo container
 */
export const LogoContainer = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${({ theme }) => theme.borders.radiusFull};
  background-color: ${({ theme }) => theme.colors.surface};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
`;

export const LogoImage = styled.img`
  width: 28px;
  height: auto;
  object-fit: contain;
`;

interface ITextContentProps {
  isCollapsed?: boolean;
}

export const TextContent = styled.div<ITextContentProps>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  max-width: ${({ isCollapsed }) => (isCollapsed ? '0' : '200px')};
  opacity: ${({ isCollapsed }) => (isCollapsed ? '0' : '1')};
  transition: max-width 0.3s ease-in-out, opacity 0.2s ease-in-out;
`;

export const BannerText = styled.span`
  ${({ theme }) => theme.typography.type102};
  color: ${({ theme }) => theme.colors.surface};
  text-transform: uppercase;
  letter-spacing: 0.02em;
  font-feature-settings: 'lnum' 1, 'pnum' 1;
`;

interface ICloseButtonProps {
  isCollapsed?: boolean;
}

export const CloseButton = styled.div<ICloseButtonProps>`
  position: absolute;
  right: -10px;
  top: 16px;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const FullWidthBanner = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: calc(var(--overlay-z-index, 1000) + 1);
  background-color: ${({ theme }) => theme.colors.textPrimary};
  padding: ${({ theme }) =>
    `${theme.spacing.spacing60} ${theme.spacing.spacing80}`};
  padding-bottom: max(${({ theme }) => theme.spacing.spacing60}, env(safe-area-inset-bottom));
  display: flex;
  align-items: center;
  justify-content: space-between;
  animation: ${slideIn} 0.3s ease-out;
`;

export const ContentWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.spacing40};
`;

export const CTAButton = styled.button`
  background-color: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textPrimary};
  border: none;
  border-radius: ${({ theme }) => theme.borders.radius20};
  padding: ${({ theme }) =>
    `${theme.spacing.spacing20} ${theme.spacing.spacing60}`};
  ${({ theme }) => theme.typography.type122};
  cursor: pointer;

  &:active {
    opacity: 0.9;
  }
`;

export const AbsoluteCloseButton = styled.button`
  position: absolute;
  top: ${({ theme }) => theme.spacing.spacing40};
  right: ${({ theme }) => theme.spacing.spacing40};
  background: transparent;
  border: none;
  padding: ${({ theme }) => theme.spacing.spacing20};
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
    color: ${({ theme }) => theme.colors.surface};
  }
`;
